import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Container, AppBar, Toolbar, Box, Typography, Button } from '@mui/material';
import { ClerkProvider, SignedIn, SignedOut, SignUp, AuthenticateWithRedirectCallback } from '@clerk/clerk-react';
import { DollarSpinner } from './components/DollarSpinner.jsx';
import UserHeader from './components/UserHeader.jsx';
import { GroupSwitcher } from './components/GroupSwitcher.jsx';
import Navigation from './components/Navigation.jsx';
import TangibleDisplay from './components/TangibleDisplay.jsx';
import { MaskingProvider } from './components/MaskingProvider.jsx';
import { CustomThemeProvider, useTheme } from './contexts/ThemeContext.jsx';
import { UiPrefsProvider } from './contexts/UiPrefsContext.jsx';
import { ToastProvider } from './components/ToastNotification.jsx';
import { UserSessionProvider } from './contexts/UserSessionContext.jsx';
import { GroupProvider } from './contexts/GroupContext.jsx';
import { GuidesProvider } from './user_guides/GuidesProvider.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import Landing from './components/Landing.jsx';
import DashboardN from './pages/DashboardN.jsx';
import Budgets from './pages/Budgets.jsx';
import BudgetDetail from './pages/BudgetDetail.jsx';
import Ledgers from './pages/Ledgers.jsx';
import LedgerDetail from './pages/LedgerDetail.jsx';
import Members from './pages/Members.jsx';
import Account from './pages/Account.jsx';
import Snapshots from './pages/Snapshots.jsx';
import PaydayFunding from './pages/PaydayFunding.jsx';
import PayeesCleanup from './pages/PayeesCleanup.jsx';
import LedgerPayees from './pages/LedgerPayees.jsx';
import AppAdmin from './pages/AppAdmin.jsx';
import FeatureSpotlightPage from './pages/FeatureSpotlightPage.jsx';
import InvitationAccept from './pages/InvitationAccept.jsx';
import Documentation from './pages/Documentation.jsx';
import DocsDock from './components/Docs/DocsDock.jsx';
import FeatureSpotlightDialog from './components/FeatureSpotlightDialog.jsx';
import TipToast from './components/TipToast.jsx';
import GroupParamGate from './components/GroupParamGate.jsx';
import { DocsDockProvider, useDocsDock, DOCK_WIDTH } from './contexts/DocsDockContext.jsx';
import PendingInvitesBanner from './components/PendingInvitesBanner.jsx';
import { BrandLockup, BrandMark } from './components/brand/BrandLogo.jsx';
import { getEnvironmentInfo, getAppBarStyling, getPulsingDotStyling } from './utils/environmentIndicator';
import { isDetailRoute } from './utils/detailRoute';
import { getConfig } from './services/configService';

const AppContent = () => {
  // #1876: the announcement dialog reports open/closed so the discovery
  // toast never competes with it.
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const { isDarkMode } = useTheme();
  const envInfo = getEnvironmentInfo();
  const location = useLocation();
  // V4_ADDENDUM convention 2: detail pages show only the Tangible metric.
  const onDetail = isDetailRoute(location.pathname);
  // When the docs reference panel is docked, shift the whole shell left by the
  // panel width on md+ so content reflows beside it instead of under it (#1064).
  const { open: docsOpen } = useDocsDock();

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  return (
    // pb clears the fixed bottom nav (56px) so the footer links are never hidden
    // behind it at full scroll (resolves #751).
    <Box
      sx={{
        flexGrow: 1,
        pb: '72px',
        transition: 'margin-right 200ms cubic-bezier(0.16, 1, 0.3, 1)',
        marginRight: { xs: 0, md: docsOpen ? `${DOCK_WIDTH}px` : 0 },
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        sx={(theme) => ({
          // Base paint first, then the non-prod striping (which overrides
          // backgroundImage with the faded stripe recipe, #1399), then positioning LAST:
          // getAppBarStyling carries a `position: relative` that must never
          // clobber the sticky bar (it did - the dev bar scrolled away while
          // the page heroes stayed pinned, #1377 review). Production renders
          // the plain bar (docs/ENVIRONMENT-INDICATORS.md, v4 board).
          backgroundColor: theme.palette.surface.bar,
          backgroundImage: 'none',
          ...(!envInfo.isProduction && getAppBarStyling(isDarkMode, envInfo)),
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          boxShadow: 'none',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.bar.text,
        })}
      >
        {/* v4 board bar (#1377): metrics LEFT, right cluster = env flag +
            workspace switcher (kept per owner decision; the board never
            modeled switching) + logo lockup + avatar. No center slot. */}
        <Toolbar
          disableGutters
          sx={{
            position: 'relative',
            zIndex: 1,
            alignItems: 'center',
            // v4 board bar height (#1401/#1405): FIXED 44px, not a
            // minimum. The sticky detail-hero offset (44px) must equal the
            // rendered bar height; a min-height let tall content (Select
            // internals) grow the bar past the offset, tucking the hero
            // underneath on scroll. Content is hard-bounded to fit.
            // Below md on a list route (#2140, epic #2138) the bar is two
            // rows: the right cluster, then the three figures; detail routes
            // keep the one 44px row their sticky heroes are pinned to.
            height: { xs: onDetail ? 44 : 'auto', md: 44 },
            minHeight: 44,
            flexDirection: { xs: onDetail ? 'row' : 'column-reverse', md: 'row' },
            px: { xs: '16px', sm: '20px', md: '28px' },
            py: '6px',
            rowGap: '6px',
          }}
        >
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start', minWidth: 0, overflow: 'hidden', width: { xs: '100%', md: 'auto' } }}>
            <TangibleDisplay detailOnly={onDetail} />
          </Box>
          <Box sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '14px', width: { xs: '100%', md: 'auto' } }}>
            {/* Env flag (#1401, kit BrdEnvBadge): burnt-sienna family so the
                chip matches the non-prod stripes (never on prod). #B25138 is
                the semantic non-production marker, not a paint token. */}
            {!envInfo.isProduction && (
              <Box
                aria-label="environment"
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: '9px',
                  py: '3px',
                  borderRadius: `${theme.ds.radius.badge}px`,
                  border: '1px solid color-mix(in srgb, #B25138 45%, transparent)',
                  backgroundColor: 'color-mix(in srgb, #B25138 12%, transparent)',
                  color: `color-mix(in srgb, #B25138 78%, ${theme.palette.text.primary})`,
                  fontSize: '10px',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                })}
              >
                <Box sx={{ ...getPulsingDotStyling(), width: 6, height: 6, backgroundColor: 'currentColor' }} />
                {envInfo.displayName}
              </Box>
            )}
            {/* The phone board's first row names the workspace switcher
                (#2140); a detail route keeps the one-row bar without it. */}
            <GroupSwitcher size="small" sx={{ minWidth: 140, display: { xs: onDetail ? 'none' : 'inline-flex', sm: 'inline-flex' } }} />
            <BrandLockup
              markSize={20}
              markVariant="bar"
              favicon
              fontSize={11}
              weight={600}
              budgetColor="bar.text"
              gap="7px"
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            />
            <BrandMark
              size={20}
              variant="bar"
              favicon
              sx={{ display: { xs: 'inline-flex', md: 'none' } }}
            />
            <UserHeader />
          </Box>
        </Toolbar>
      </AppBar>

      <Navigation />

      <PendingInvitesBanner />

      {/* No top gap (#1401): the v4 board sits the page hero flush under the
          bar, hairline to hairline; the hero owns its 13px internal padding. */}
      {/* #1710: consume an email deep link's ?group= before any page renders,
          so the routed page never mounts against the previous workspace. */}
      <Container maxWidth="lg" sx={{ mb: 4 }}>
        <GroupParamGate>
          <Routes>
            {/* #2179: Dashboard_N is the Dashboard; the classic page retired. */}
            <Route path="/" element={<DashboardN isAuthReady={true} />} />
            <Route path="/budgets" element={<Budgets isAuthReady={true} />} />
            <Route path="/budgets/:id" element={<BudgetDetail isAuthReady={true} />} />
            <Route path="/ledgers" element={<Ledgers isAuthReady={true} />} />
            <Route path="/ledgers/payees" element={<LedgerPayees isAuthReady={true} />} />
            <Route path="/ledgers/:id" element={<LedgerDetail isAuthReady={true} />} />
            <Route path="/members" element={<Members isAuthReady={true} />} />
            <Route path="/account" element={<Account isAuthReady={true} />} />
            <Route path="/snapshots" element={<Snapshots isAuthReady={true} />} />
            {/* Back-compat: old /alerts path redirects to the renamed route (#783) */}
            <Route path="/alerts" element={<Navigate to="/snapshots" replace />} />
            <Route path="/payday-funding" element={<PaydayFunding isAuthReady={true} />} />
            <Route path="/payees/cleanup" element={<PayeesCleanup isAuthReady={true} />} />
            <Route path="/invitations/:inviteId" element={<InvitationAccept isAuthReady={true} />} />
            <Route path="/docs" element={<Documentation isAuthReady={true} />} />
            {/* #1427: application-admin page; non-admins are redirected inside
                the page (convention 13). */}
            <Route path="/admin" element={<AppAdmin isAuthReady={true} />} />
            {/* #1876: Feature Spotlight archive, reached from Help > What's new. */}
            <Route path="/updates" element={<FeatureSpotlightPage isAuthReady={true} />} />
          </Routes>
        </GroupParamGate>
      </Container>

      {/* V4_ADDENDUM convention 9 (#1377): the global footer is gone - the
          Privacy/Terms links render ONLY at the Dashboard scroll end and on
          the Account page (ds/FooterLinks). The signed-out invite gate keeps
          its own copy below. */}

      {/* Dockable documentation reference panel (#1064); always mounted so it animates */}
      <DocsDock />

      {/* Feature Spotlight announcement (#1876); mounted once, opens at most
          once per session, and holds the discovery toast back while open */}
      <FeatureSpotlightDialog onOpenChange={setSpotlightOpen} />

      {/* Feature-discovery toast (#1717); mounted once so an open toast
          survives route changes for its display window */}
      <TipToast suppressed={spotlightOpen} />
    </Box>
  );
};

const AuthenticatedApp = () => {
  // The guide engine is the most dependent context (it reads the user session,
  // the active group (sandbox swap), and the API) so it sits innermost.
  return (
    <UserSessionProvider>
      <GroupProvider>
        <GuidesProvider>
          <ToastProvider>
            <MaskingProvider>
              <DocsDockProvider>
                <AppContent />
              </DocsDockProvider>
            </MaskingProvider>
          </ToastProvider>
        </GuidesProvider>
      </GroupProvider>
    </UserSessionProvider>
  );
};

const SignedOutGate = () => {
  const path = typeof window !== 'undefined' ? window.location.pathname + window.location.search : '/';
  const isInviteLanding = path.startsWith('/invitations/');
  if (isInviteLanding) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Box flex="1" display="flex" justifyContent="center" alignItems="center">
          <SignUp
            signInUrl="/sign-in"
            afterSignUpUrl={path}
            afterSignInUrl={path}
          />
        </Box>
        <Box component="footer" sx={{ py: 2, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            <a href="/privacy" style={{ color: 'inherit', textDecoration: 'underline' }}>Privacy Policy</a>
            {' · '}
            <a href="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>Terms of Service</a>
          </Typography>
        </Box>
      </Box>
    );
  }
  return <Landing />;
};

function App() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoading(true);
        setError(null);

        const cfg = await getConfig();
        setConfig(cfg);
      } catch (err) {
        console.error('❌ Failed to load configuration:', err);
        setError(err.message);

        if (retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000;
          setTimeout(() => {
            setRetryCount(retryCount + 1);
          }, delay);
        } else {
          console.error('❌ Max retry attempts reached');
        }
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [retryCount]);

  if (loading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
      >
        <DollarSpinner size="large" />
        <Typography variant="h6" color="textSecondary">
          Loading configuration...
        </Typography>
        {retryCount > 0 && (
          <Typography variant="body2" color="textSecondary">
            Retry attempt {retryCount + 1}/3
          </Typography>
        )}
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        gap={2}
        px={3}
      >
        <Typography variant="h5" color="error" gutterBottom>
          Failed to Load Configuration
        </Typography>
        <Typography variant="body1" color="textSecondary" align="center" sx={{ maxWidth: 600 }}>
          {error}
        </Typography>
        <Typography variant="body2" color="textSecondary" align="center">
          Please check your network connection and try again.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => {
            setRetryCount(0);
            setError(null);
          }}
          sx={{ mt: 2 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  const isSsoCallback = typeof window !== 'undefined' && window.location.pathname === '/sso-callback';

  return (
    <ClerkProvider publishableKey={config.authPublishableKey}>
      <ErrorBoundary title="Application Error">
        <CustomThemeProvider>
          <UiPrefsProvider>
            {isSsoCallback ? (
              <AuthenticateWithRedirectCallback
                signInFallbackRedirectUrl="/"
                signUpFallbackRedirectUrl="/"
              />
            ) : (
              <>
                <SignedIn>
                  <AuthenticatedApp />
                </SignedIn>
                <SignedOut>
                  <SignedOutGate />
                </SignedOut>
              </>
            )}
          </UiPrefsProvider>
        </CustomThemeProvider>
      </ErrorBoundary>
    </ClerkProvider>
  );
}

export default App;
